/**
 * One Escape for every popup, dropdown, popover and modal (TZ §9).
 *
 * Layers register on open; a single capture-phase keydown listener sends
 * Escape to the TOP layer only. The layer decides what closing means — a
 * dirty form shows its "Discard changes?" confirm (itself a layer, so the
 * next Escape closes only the confirm). Focus goes back to the element
 * that was focused when the layer opened.
 *
 * Nothing here knows React; `useLayer` in the same file is the hook that
 * components call. Scattered `keydown` handlers are gone on purpose: two
 * listeners answering one key is how a nested dropdown and its modal both
 * closed at once.
 */

import { useEffect, useRef } from "react";

type Layer = {
  id: number;
  onRequestClose: () => void;
  returnFocus: HTMLElement | null;
};

const stack: Layer[] = [];
let nextId = 1;
let installed = false;

export function handleEscapeKey(e: { key: string; preventDefault: () => void; stopPropagation: () => void }): void {
  if (e.key !== "Escape" || stack.length === 0) return;
  const top = stack[stack.length - 1];
  e.preventDefault();
  e.stopPropagation();
  top.onRequestClose();
}

function install(): void {
  if (installed || typeof window === "undefined") return;
  window.addEventListener("keydown", handleEscapeKey, true);
  installed = true;
}

function activeElement(): HTMLElement | null {
  const active = typeof document !== "undefined" ? document.activeElement : null;
  return typeof HTMLElement !== "undefined" && active instanceof HTMLElement ? active : null;
}

/** `returnFocus` is the element to focus again when this layer closes;
 *  defaults to whatever is focused right now. */
export function pushLayer(onRequestClose: () => void, returnFocus?: HTMLElement | null): number {
  install();
  const id = nextId++;
  stack.push({
    id,
    onRequestClose,
    returnFocus: returnFocus === undefined ? activeElement() : returnFocus,
  });
  return id;
}

export function updateLayer(id: number, onRequestClose: () => void): void {
  const l = stack.find((x) => x.id === id);
  if (l) l.onRequestClose = onRequestClose;
}

export function popLayer(id: number): void {
  const i = stack.findIndex((x) => x.id === id);
  if (i < 0) return;
  const [l] = stack.splice(i, 1);
  // Only the top layer gives focus back — a parent closing under an open
  // child would otherwise steal focus from the child.
  if (i === stack.length && l.returnFocus && l.returnFocus.isConnected) {
    l.returnFocus.focus();
  }
}

export function layerDepth(): number {
  return stack.length;
}

/** Register `open` as a layer. `onRequestClose` runs on Escape for the top
 *  layer; put the dirty check inside it (show a confirm instead of closing).
 *  The latest callback is always used — no stale closures. */
export function useLayer(open: boolean, onRequestClose: () => void): void {
  const cb = useRef(onRequestClose);
  cb.current = onRequestClose;
  // Focus is captured while the layer is still closed (or on the very first
  // render of a layer that mounts already open): by the time the effect
  // runs, an autoFocus input inside the layer would already own it.
  const focusBefore = useRef<HTMLElement | null>(null);
  if (!open || focusBefore.current === null) focusBefore.current = activeElement();
  useEffect(() => {
    if (!open) return;
    const id = pushLayer(() => cb.current(), focusBefore.current);
    return () => popLayer(id);
  }, [open]);
}
